import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, CheckCircle2, XCircle, AlertCircle, Plus, Filter,
  ChevronLeft, ChevronRight, Search, Bell, Settings, BarChart2,
  ArrowLeft, X, Check, Edit2, Trash2, Send, User, Phone, FileText,
  Stethoscope
} from 'lucide-react';
import { 
  getMyAppointments, getTenantAppointments,
  updateAppointmentStatus 
} from '../services/attendanceApi';
import { getProfessionals } from '../services/professionalApi';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

// =============================================================================
// Horários da grade
// =============================================================================
const horariosGrade = [
  '08:00','08:30','09:00','09:30','10:00','10:30',
  '11:00','11:30','12:00','12:30','13:00','13:30',
  '14:00','14:30','15:00','15:30','16:00','16:30',
  '17:00','17:30','18:00'
];

// =============================================================================
// Componente Modal
// =============================================================================
function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fadeIn" onClick={onClose}>
      <div 
        className={`bg-white dark:bg-slate-800 rounded-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'} shadow-2xl animate-popIn`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-slate-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">{title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-500">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// =============================================================================
// Componente Toast
// =============================================================================
function Toast({ msg, onClose, type = 'success' }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-blue-600'
  };

  return (
    <div className={`fixed bottom-6 right-6 ${colors[type]} text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 z-50 animate-slideIn`}>
      {type === 'success' && <Check size={16} />}
      {type === 'error' && <XCircle size={16} />}
      {type === 'info' && <Bell size={16} />}
      <span className="font-medium text-sm">{msg}</span>
    </div>
  );
}

// =============================================================================
// Sub-página: Estatísticas
// =============================================================================
function EstatisticasPage({ onBack, appointments, t }) {
  const total = appointments.length;
  const confirmados = appointments.filter(a => a.status === 'confirmed').length;
  const concluidos = appointments.filter(a => a.status === 'completed').length;
  const agendados = appointments.filter(a => a.status === 'scheduled').length;
  const faltaram = appointments.filter(a => a.status === 'no_show').length;
  const cancelados = appointments.filter(a => a.status === 'cancelled').length;
  const taxa = total > 0 ? Math.round(((confirmados + concluidos) / total) * 100) : 0;

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300">
        <ArrowLeft size={16} /> {t('common.back')}
      </button>

      <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">📊 {t('appointments.scheduleStatistics')}</h2>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: t('appointments.total'), value: total, color: 'blue' },
          { label: t('appointments.confirmed'), value: confirmados, color: 'green' },
          { label: t('appointments.scheduled'), value: agendados, color: 'indigo' },
          { label: t('appointments.completed'), value: concluidos, color: 'purple' },
          { label: t('appointments.attendanceRate'), value: `${taxa}%`, color: 'amber' }
        ].map(s => (
          <div key={s.label} className={`p-5 rounded-2xl bg-${s.color}-50 dark:bg-${s.color}-900/20 border border-${s.color}-200 dark:border-${s.color}-800`}>
            <div className={`text-3xl font-bold text-${s.color}-900 dark:text-${s.color}-300`}>{s.value}</div>
            <div className={`text-xs font-medium text-${s.color}-700 dark:text-${s.color}-400 mt-1`}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-6">
        <h4 className="text-base font-bold text-gray-900 dark:text-slate-100 mb-5">{t('appointments.statusDistribution')}</h4>
        <div className="space-y-4">
          {[
            { label: t('appointments.confirmed'), count: confirmados, total, color: '#059669' },
            { label: t('appointments.scheduled'), count: agendados, total, color: '#4f46e5' },
            { label: t('appointments.completed'), count: concluidos, total, color: '#7c3aed' },
            { label: t('appointments.noShow'), count: faltaram, total, color: '#ea580c' },
            { label: t('appointments.cancelled'), count: cancelados, total, color: '#e11d48' }
          ].map(b => (
            <div key={b.label} className="flex items-center gap-3">
              <span className="w-28 text-sm text-gray-600 dark:text-slate-400">{b.label}</span>
              <div className="flex-1 bg-gray-100 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${b.total ? (b.count / b.total) * 100 : 0}%`, background: b.color }}
                />
              </div>
              <span className="w-8 text-right text-sm font-bold text-gray-700 dark:text-slate-300">{b.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
function AppointmentsPage() {
  const { t } = useLanguage();
  
  const today = new Date().toISOString().slice(0, 10);
  const [view, setView] = useState('agenda');
  const [selectedDate, setSelectedDate] = useState(today);
  const [appointments, setAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalObs, setModalObs] = useState(null);
  const [modalLembrete, setModalLembrete] = useState(null);
  const [editingObs, setEditingObs] = useState('');
  const [novoForm, setNovoForm] = useState({
    horario: '', contact_name: '', contact_phone: '',
    appointment_type: 'consulta', notes: '', professional_id: ''
  });

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  // Status config traduzido
  const statusConfig = {
    scheduled: { label: t('appointments.scheduled'), color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Calendar },
    confirmed: { label: t('appointments.confirmed'), color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
    completed: { label: t('appointments.completed'), color: 'bg-purple-100 text-purple-700 border-purple-200', icon: CheckCircle2 },
    cancelled: { label: t('appointments.cancelled'), color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
    no_show: { label: t('appointments.noShow'), color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle }
  };

  // Carregar dados iniciais
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUser(payload);
    }
    loadProfessionals();
  }, []);

  useEffect(() => {
    loadAppointments();
  }, [selectedDate, selectedProfessional, currentUser]);

  const loadProfessionals = async () => {
    try {
      const res = await getProfessionals();
      setProfessionals(res.data);
      if (res.data.length > 0 && !selectedProfessional) {
        setSelectedProfessional(res.data[0]);
      }
    } catch (err) {
      console.error('Erro ao carregar profissionais:', err);
    }
  };

  const loadAppointments = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      // ⭐ CORRIGIDO: usa a string da data diretamente, sem passar por Date/setHours
      // (que misturava interpretação UTC com hora local e deslocava o dia)
      const isAdmin = ['tenant_admin', 'master_admin', 'manager'].includes(currentUser?.role);
      
      let res;
      if (isAdmin) {
        res = await getTenantAppointments(selectedDate, selectedDate);
      } else {
        res = await getMyAppointments(selectedDate, selectedDate);
      }

      let data = res.data || [];
      
      if (selectedProfessional && isAdmin) {
        data = data.filter(a => a.professional_id === selectedProfessional.id);
      }

      setAllAppointments(data);
      setAppointments(data);
    } catch (err) {
      console.error('Erro ao carregar agendamentos:', err);
      showToast(t('appointments.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateAppointmentStatus(id, newStatus);
      setAppointments(appointments.map(a => 
        a.id === id ? { ...a, status: newStatus } : a
      ));
      setAllAppointments(allAppointments.map(a => 
        a.id === id ? { ...a, status: newStatus } : a
      ));
      showToast(`${t('appointments.statusUpdated')} "${statusConfig[newStatus]?.label || newStatus}"`);
    } catch (err) {
      showToast(t('appointments.errorUpdateStatus'), 'error');
    }
  };

  const handleSaveObs = async () => {
    try {
      await api.put(`/api/appointments/${modalObs.id}`, { notes: editingObs });
      setAppointments(appointments.map(a => 
        a.id === modalObs.id ? { ...a, notes: editingObs } : a
      ));
      setAllAppointments(allAppointments.map(a => 
        a.id === modalObs.id ? { ...a, notes: editingObs } : a
      ));
      setModalObs(null);
      showToast(t('appointments.observationSaved'));
    } catch (err) {
      showToast(t('appointments.errorSaveObs'), 'error');
    }
  };

  const handleCreateAppointment = async () => {
    if (!novoForm.contact_name || !novoForm.horario) {
      showToast(t('appointments.fillRequiredFields'), 'error');
      return;
    }

    try {
      const [hours, minutes] = novoForm.horario.split(':');
      const appointmentDate = new Date(selectedDate);
      appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const payload = {
        contact_name: novoForm.contact_name,
        contact_phone: novoForm.contact_phone,
        appointment_date: appointmentDate.toISOString(),
        appointment_type: novoForm.appointment_type,
        notes: novoForm.notes,
        professional_id: selectedProfessional?.id || null
      };

      await api.post('/api/appointments', payload);
      await loadAppointments();
      setNovoForm({
        horario: '', contact_name: '', contact_phone: '',
        appointment_type: 'consulta', notes: '', professional_id: ''
      });
      setModalNovo(false);
      showToast(t('appointments.createdSuccess'));
    } catch (err) {
      showToast(t('appointments.errorCreate') + ': ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleRemoveAppointment = async (id) => {
    if (!window.confirm(t('appointments.confirmDelete'))) return;
    try {
      await api.delete(`/api/appointments/${id}`);
      await loadAppointments();
      showToast(t('appointments.deletedSuccess'));
    } catch (err) {
      showToast(t('appointments.errorDelete'), 'error');
    }
  };

  const handleSendLembrete = async (apt, channel = 'whatsapp') => {
    try {
      await api.post(`/api/appointments/${apt.id}/send-reminder`, { channel });
      setModalLembrete(null);
      showToast(`${t('appointments.reminderSent')} ${apt.contact_name}!`);
    } catch (err) {
      showToast(t('appointments.errorSendReminder'), 'error');
    }
  };

  const handleSendAllReminders = async () => {
    const pendentes = appointments.filter(a => 
      a.status === 'scheduled' || a.status === 'confirmed'
    );
    if (pendentes.length === 0) {
      showToast(t('appointments.noPending'), 'info');
      return;
    }
    if (!window.confirm(`${t('appointments.confirmSendReminders')} ${pendentes.length} ${t('appointments.appointmentsLower')}?`)) return;

    let enviados = 0;
    for (const apt of pendentes) {
      try {
        await api.post(`/api/appointments/${apt.id}/send-reminder`, { channel: 'whatsapp' });
        enviados++;
      } catch (err) {
        console.error(`Falha ao enviar para ${apt.contact_name}`, err);
      }
    }
    showToast(`${t('appointments.remindersSent')} ${enviados} ${t('appointments.patients')}!`);
  };

  const changeDate = (dir) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + dir);
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const match = String(dateStr).match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '';
  };

  const filtered = appointments.filter(a => {
    if (search) {
      const s = search.toLowerCase();
      if (
        !(a.contact_name || '').toLowerCase().includes(s) &&
        !(a.contact_phone || '').includes(s)
      ) return false;
    }
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    return true;
  });

  const gradeComAgendamentos = horariosGrade.map(h => ({
    horario: h,
    agendamento: filtered.find(a => formatTime(a.appointment_date) === h) || null
  }));

  const stats = {
    total: appointments.length,
    scheduled: appointments.filter(a => a.status === 'scheduled').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    cancelled: appointments.filter(a => a.status === 'cancelled').length,
    no_show: appointments.filter(a => a.status === 'no_show').length
  };

  if (view === 'stats') {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EstatisticasPage onBack={() => setView('agenda')} appointments={allAppointments} t={t} />
        {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    );
  }

  const isAdmin = ['tenant_admin', 'master_admin', 'manager'].includes(currentUser?.role);

  return (
        <div className="p-6 w-full space-y-6">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 pb-5 border-b border-gray-200 dark:border-slate-700">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Calendar size={28} className="text-purple-600" />
            {t('appointments.title')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">{t('appointments.subtitle')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button 
            onClick={() => window.location.href = '/professional-config'}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
            title={t('appointments.registerProfessionals')}
          >
            <Settings size={16} />
            {t('appointments.professionals')}
          </button>
          
          <button onClick={() => setView('stats')} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600">
            <BarChart2 size={16} /> {t('appointments.statistics')}
          </button>
          <button onClick={handleSendAllReminders} className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-sm font-bold hover:bg-amber-100 dark:hover:bg-amber-900/30">
            <Bell size={16} /> {t('appointments.sendReminders')}
          </button>
          <button onClick={() => setModalNovo(true)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700 shadow-lg shadow-purple-100 dark:shadow-none">
            <Plus size={16} /> {t('appointments.newAppointment')}
          </button>
        </div>
      </div>

      {/* Seletor de Profissional — dropdown */}
      <div className="relative max-w-md">
        <select
          value={selectedProfessional?.id || ''}
          onChange={(e) => {
            const prof = professionals.find(p => p.id === parseInt(e.target.value));
            setSelectedProfessional(prof || null);
          }}
          className="w-full appearance-none px-4 py-3 pr-10 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl text-sm font-bold text-gray-900 dark:text-slate-100 focus:border-purple-500 outline-none cursor-pointer"
          style={selectedProfessional ? { borderColor: selectedProfessional.color } : {}}
        >
          {professionals.map(prof => (
            <option key={prof.id} value={prof.id}>
              {prof.name} — {prof.specialty || t('appointments.noSpecialty')}
            </option>
          ))}
        </select>
        <div
          className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-white flex items-center justify-center font-bold text-xs pointer-events-none"
          style={{ background: selectedProfessional?.color || '#7c3aed' }}
        >
          {selectedProfessional ? selectedProfessional.name.charAt(0).toUpperCase() : '?'}
        </div>
      </div>

      {/* Barra do profissional selecionado + Navegação de data */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div 
            className="w-14 h-14 rounded-full text-white flex items-center justify-center font-bold text-xl"
            style={{ background: selectedProfessional?.color || '#7c3aed' }}
          >
            {selectedProfessional ? selectedProfessional.name.charAt(0).toUpperCase() : 'T'}
          </div>
          <div>
            <div className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
              {selectedProfessional ? t('appointments.professional') : t('appointments.allProfessionals')}
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-slate-100">
              {selectedProfessional?.name || t('appointments.fullSchedule')}
            </div>
            {selectedProfessional?.specialty && (
              <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                {selectedProfessional.specialty}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => changeDate(-1)} className="w-9 h-9 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 flex items-center justify-center text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600">
            <ChevronLeft size={18} />
          </button>
                    <div className="relative flex items-center gap-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg px-4 py-2">
            <Calendar size={15} className="text-gray-500 dark:text-slate-400 pointer-events-none" />
            {/* ⭐ NOVO: texto formatado em DD/MM/AAAA por cima, já que o campo nativo
                às vezes ignora o idioma e mostra em formato americano */}
            <span className="text-sm font-medium text-gray-900 dark:text-slate-100 pointer-events-none">
              {selectedDate ? selectedDate.split('-').reverse().join('/') : ''}
            </span>
            <input
              type="date"
              lang="pt-BR"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </div>
          <button onClick={() => changeDate(1)} className="w-9 h-9 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 flex items-center justify-center text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Resumo do dia + Filtros */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-400 flex-wrap">
          <Calendar size={15} />
          <strong className="text-gray-900 dark:text-slate-100 capitalize">{formatDate(selectedDate)}</strong>
          <span>•</span>
          <span>{appointments.length} {t('appointments.appointmentsLower')}</span>
          <span>•</span>
          <span className="text-green-600 dark:text-green-400 font-semibold">{stats.confirmed} {t('appointments.confirmedLower')}</span>
          <span>•</span>
          <span className="text-orange-600 dark:text-orange-400 font-semibold">{stats.no_show} {t('appointments.noShows')}</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('appointments.searchPatient')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none focus:border-purple-500 w-48 text-gray-900 dark:text-slate-100"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none text-gray-900 dark:text-slate-100"
          >
            <option value="all">{t('appointments.all')}</option>
            <option value="scheduled">{t('appointments.scheduled')}</option>
            <option value="confirmed">{t('appointments.confirmed')}</option>
            <option value="completed">{t('appointments.completed')}</option>
            <option value="no_show">{t('appointments.noShow')}</option>
            <option value="cancelled">{t('appointments.cancelled')}</option>
          </select>
        </div>
      </div>

      {/* Tabela de Agenda */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-900 dark:bg-slate-950">
              <tr>
                <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider w-24">{t('appointments.time')}</th>
                <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">{t('appointments.patient')}</th>
                <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">{t('appointments.contact')}</th>
                <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">{t('appointments.type')}</th>
                             <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">Tratamento</th>
                <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">Pagamento</th>            
  <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">{t('appointments.observation')}</th>
                <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">{t('appointments.status')}</th>
                <th className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider">{t('appointments.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                                    <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                    {t('common.loading')}
                  </td>
                </tr>
              ) : (
                gradeComAgendamentos.map(({ horario, agendamento }) => {
                  if ((search || filterStatus !== 'all') && !agendamento) return null;

                  const config = agendamento ? (statusConfig[agendamento.status] || statusConfig.scheduled) : null;
                  const Icon = config?.icon;

                  return (
                    <tr
                      key={horario}
                      className={`border-t border-gray-100 dark:border-slate-700 transition-colors ${
                        !agendamento ? 'bg-gray-50 dark:bg-slate-900/30 opacity-50' :
                        agendamento.status === 'confirmed' ? 'bg-green-50/30 dark:bg-green-900/10' :
                        agendamento.status === 'no_show' ? 'bg-orange-50/30 dark:bg-orange-900/10' :
                        agendamento.status === 'cancelled' ? 'bg-red-50/30 dark:bg-red-900/10 opacity-70' :
                        'bg-white dark:bg-slate-800 hover:bg-purple-50/30 dark:hover:bg-purple-900/10'
                      }`}
                    >
                      <td className="px-2 py-3">
                        <div className="font-bold text-gray-700 dark:text-slate-300 text-sm">{horario}</div>
                      </td>
                      <td className="px-2 py-3">
                        {agendamento ? (
                          <div className="flex items-center gap-2">
                                                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                              {(agendamento.contact_name || '?').charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-slate-100 text-sm whitespace-nowrap">
                              {agendamento.contact_name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-300 dark:text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400 font-mono">
                        {agendamento?.contact_phone || ''}
                      </td>
                                            <td className="px-2 py-3">
                        {agendamento && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-full text-xs font-bold capitalize">
                            {agendamento.type === 'avaliacao' ? t('appointments.typeEvaluation') :
                             agendamento.type === 'tratamento' ? t('appointments.typeTreatment') :
                             agendamento.type === 'retorno' ? t('appointments.typeReturn') :
                             agendamento.type === 'exame' ? t('appointments.typeExam') :
                             agendamento.type === 'procedimento' ? t('appointments.typeProcedure') :
                             agendamento.type === 'urgencia' ? t('appointments.typeUrgency') :
                             t('appointments.typeConsultation')}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {agendamento && (
                          <span className="text-sm text-gray-700 dark:text-slate-300">
                            {agendamento.title || '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {agendamento && (
                          <span className="text-sm text-gray-700 dark:text-slate-300">
                            {agendamento.payment_info || '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {agendamento && (
                          <div className="flex items-center gap-1">
                            <span className={agendamento.notes ? 'text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded font-medium max-w-[150px] truncate' : 'text-xs text-gray-300 dark:text-slate-600'}>
                              {agendamento.notes || '—'}
                            </span>
                            <button 
                              onClick={() => { setModalObs(agendamento); setEditingObs(agendamento.notes || ''); }}
                              className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
                            >
                              <Edit2 size={12} />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {agendamento && (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${config.color}`}>
                            <Icon size={12} />
                            {config.label}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {agendamento && (
                          <div className="flex items-center gap-1">
                            <select
                              value={agendamento.status}
                              onChange={(e) => handleStatusChange(agendamento.id, e.target.value)}
                              className="px-2 py-1 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-xs outline-none focus:border-purple-500 text-gray-900 dark:text-slate-100"
                            >
                              <option value="scheduled">{t('appointments.scheduled')}</option>
                              <option value="confirmed">{t('appointments.confirmed')}</option>
                              <option value="completed">{t('appointments.completed')}</option>
                              <option value="no_show">{t('appointments.noShow')}</option>
                              <option value="cancelled">{t('appointments.cancelled')}</option>
                            </select>
                            <button
                              onClick={() => setModalLembrete(agendamento)}
                              className="p-1.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg"
                              title={t('appointments.sendReminder')}
                            >
                              <Bell size={14} />
                            </button>
                            <button
                              onClick={() => handleRemoveAppointment(agendamento.id)}
                              className="p-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                              title={t('common.delete')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Novo Agendamento */}
      {modalNovo && (
        <Modal title={t('appointments.newAppointment')} onClose={() => setModalNovo(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">{t('appointments.time')} *</label>
              <select
                value={novoForm.horario}
                onChange={(e) => setNovoForm({...novoForm, horario: e.target.value})}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              >
                <option value="">{t('appointments.select')}</option>
                {horariosGrade.filter(h => !appointments.find(a => formatTime(a.appointment_date) === h)).map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">{t('appointments.patient')} *</label>
              <input
                type="text"
                value={novoForm.contact_name}
                onChange={(e) => setNovoForm({...novoForm, contact_name: e.target.value})}
                placeholder={t('appointments.fullName')}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">{t('appointments.phone')}</label>
              <input
                type="text"
                value={novoForm.contact_phone}
                onChange={(e) => setNovoForm({...novoForm, contact_phone: e.target.value})}
                placeholder="(00) 00000-0000"
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">{t('appointments.type')}</label>
              <select
                value={novoForm.appointment_type}
                onChange={(e) => setNovoForm({...novoForm, appointment_type: e.target.value})}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              >
                                <option value="consulta">{t('appointments.typeConsultation')}</option>
                <option value="retorno">{t('appointments.typeReturn')}</option>
                <option value="exame">{t('appointments.typeExam')}</option>
                <option value="procedimento">{t('appointments.typeProcedure')}</option>
                <option value="urgencia">{t('appointments.typeUrgency')}</option>
                <option value="tratamento">{t('appointments.typeTreatment')}</option>
                <option value="avaliacao">{t('appointments.typeEvaluation')}</option>
                <option value="tratamento">{t('appointments.typeTreatment')}</option>
                <option value="avaliacao">{t('appointments.typeEvaluation')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">{t('appointments.observation')}</label>
              <textarea
                value={novoForm.notes}
                onChange={(e) => setNovoForm({...novoForm, notes: e.target.value})}
                rows={3}
                placeholder={t('appointments.observationPlaceholder')}
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 resize-none"
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button onClick={() => setModalNovo(false)} className="px-5 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-600">
                {t('common.cancel')}
              </button>
              <button onClick={handleCreateAppointment} className="px-5 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700">
                {t('appointments.schedule')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Editar Observação */}
      {modalObs && (
        <Modal title={t('appointments.observation')} onClose={() => setModalObs(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {t('appointments.patient')}: <strong className="text-gray-900 dark:text-slate-100">{modalObs.contact_name}</strong>
            </p>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">{t('appointments.observation')}</label>
              <textarea
                value={editingObs}
                onChange={(e) => setEditingObs(e.target.value)}
                rows={4}
                placeholder={t('appointments.observationPlaceholder')}
                className="w-full p-3 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 resize-none"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModalObs(null)} className="px-5 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-600">
                {t('common.cancel')}
              </button>
              <button onClick={handleSaveObs} className="px-5 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700">
                {t('common.save')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Enviar Lembrete */}
      {modalLembrete && (
        <Modal title={t('appointments.sendReminder')} onClose={() => setModalLembrete(null)}>
          <div className="space-y-4">
            <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center font-bold">
                {(modalLembrete.contact_name || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-bold text-gray-900 dark:text-slate-100">{modalLembrete.contact_name}</div>
                <div className="text-sm text-gray-600 dark:text-slate-400">{modalLembrete.contact_phone}</div>
                <div className="text-xs text-gray-500 dark:text-slate-500 flex items-center gap-1 mt-1">
                  <Clock size={12} />
                  {formatTime(modalLembrete.appointment_date)} • {formatDate(selectedDate)}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-2">{t('appointments.messageToBeSent')}:</p>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 text-sm text-gray-800 dark:text-slate-200 leading-relaxed">
                {t('appointments.reminderMessage', { 
                  name: modalLembrete.contact_name?.split(' ')[0],
                  date: formatDate(selectedDate),
                  time: formatTime(modalLembrete.appointment_date)
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-2">{t('appointments.sendVia')}:</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleSendLembrete(modalLembrete, 'whatsapp')}
                  className="flex flex-col items-center gap-2 p-3 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-xl font-bold text-sm hover:bg-green-100 dark:hover:bg-green-900/30"
                >
                  💬 WhatsApp
                </button>
                <button
                  onClick={() => handleSendLembrete(modalLembrete, 'sms')}
                  className="flex flex-col items-center gap-2 p-3 border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-xl font-bold text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30"
                >
                  📱 SMS
                </button>
                <button
                  onClick={() => handleSendLembrete(modalLembrete, 'email')}
                  className="flex flex-col items-center gap-2 p-3 border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-xl font-bold text-sm hover:bg-purple-100 dark:hover:bg-purple-900/30"
                >
                  ✉️ E-mail
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Animações CSS */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes popIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: translateY(12px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.15s ease; }
        .animate-popIn { animation: popIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .animate-slideIn { animation: slideIn 0.25s ease; }
      `}</style>
    </div>
  );
}

export default AppointmentsPage;
